import s from './cache.module.styl'
import React, { useEffect, useRef } from 'react'
import t from '~t'
import { API_ENDPOINT_URL } from '~data/constants/app'
import { useSelector, useDispatch } from 'react-redux'
import { isPro } from '~data/selectors/user'
import { oneLoad } from '~data/actions/bookmarks/single'

import Header, { Title, Space } from '~co/common/header'
import Button from '~co/common/button'
import Icon from '~co/common/icon'
import Alert from '~co/common/alert'
import { APP_DOMAIN } from '~data/constants/app'


const invalidStatus = {
    'invalid-origin': 'Origin is unreachable.',
    'invalid-size': 'Page size too large.',
    'invalid-timeout': 'Timeout.',
    'failed': 'Parse failed.'
}

const PENDING_STATUSES = new Set(['', 'retry'])
const POLL_INTERVAL_MS = 4000

function CacheStatus({ cache, url, onRetry }) {
    let icon = '', title = ''

    switch(cache) {
        case 'ready':
            icon = 'ready'
            title = t.s('done')
        break

        case 'retry':
        case '':
            icon = 'retry'
            title = 'Not available yet...'
        break

        default:
            icon = 'failed'
            title = <>{t.s('supportOnlyUrls')} <b>{invalidStatus[cache]}</b></>
        break
    }

    return (
        <Header className={s.status} data-status={cache}>
            {icon && <Icon name={'cache_'+icon} className={s.icon} />}
            <Title>
                {title}<br/>
                <small>
                    Permanent copies are made automatically for all bookmarks — you don't need to do anything. <a href={`https://help.${APP_DOMAIN}/permanent-copy`} target='_blank'>Learn more</a>
                </small>
            </Title>
            <Space />

            {cache == 'ready' ? (
                <>
                    <Button 
                        href={url}
                        rel='noopener'
                        target='_blank'>
                        <Icon name='open' size='micro' />
                        {t.s('open')}
                    </Button>

                    <Button href={url+'?download'}>
                        <Icon name='document' size='micro' />
                        {t.s('download')}
                    </Button>
                </>
            ) : (
                <Button onClick={onRetry}>
                    <Icon name='refresh' size='micro' />
                    {t.s('retry')}
                </Button>
            )}
        </Header>
    )
}

export default function PageMyItemTabCache({ item: { cache, _id } }) {
    const pro = useSelector(state=>isPro(state))
    const dispatch = useDispatch()
    const url = `${API_ENDPOINT_URL}askwise/${_id}/cache`
    const pollRef = useRef(null)

    const triggerAndRefresh = React.useCallback((force=false)=>{
        const fetchUrl = force ? `${url}?force=true` : url
        fetch(fetchUrl, { credentials: 'include' })
            .then(()=>dispatch(oneLoad(_id)))
            .catch(()=>{})
    }, [_id, url])

    // Trigger cache fetch on mount and poll for status while pending
    useEffect(()=>{
        // Always hit the endpoint once — this triggers the background job server-side
        triggerAndRefresh()

        if (!PENDING_STATUSES.has(cache)) return

        pollRef.current = setInterval(async ()=>{
            try {
                const res = await fetch(url, { credentials: 'include' })
                if (!res.ok) return
                const contentType = res.headers.get('content-type') || ''
                if (contentType.includes('text/html')) {
                    // Cache is now ready — reload the bookmark from the store
                    clearInterval(pollRef.current)
                    dispatch(oneLoad(_id))
                    return
                }
                const data = await res.json()
                const newCache = data?.item?.cache
                if (newCache && !PENDING_STATUSES.has(newCache)) {
                    clearInterval(pollRef.current)
                    dispatch(oneLoad(_id))
                }
            } catch(e) { /* ignore network errors */ }
        }, POLL_INTERVAL_MS)

        return ()=>clearInterval(pollRef.current)
    }, [_id, cache])

    // if (!pro)
    //     return (
    //         <Alert variant='warning'>
    //             {t.s('onlyInPro')}
    //         </Alert>
    //     )

    switch(cache) {
        case 'ready':
            return (
                <div className={s.cache}>
                    <CacheStatus cache={cache} url={url} onRetry={()=>triggerAndRefresh(true)} />
                    <iframe 
                        className={s.frame} 
                        plugins='true'
                        src={url} />
                </div>
            )
        
        default:
            return <CacheStatus cache={cache} url={url} onRetry={()=>triggerAndRefresh(true)} />
    }
}